import Button from '@orn/shared-web/components/Button/Button';
import { RadioButtonCard, RadioGroup } from '@orn/shared-web/components/RadioButton/RadioButtons';

import type { FinancialAccountSubtype } from '../../../__generated__/globalTypes';

export interface IPaymentAccountOption {
  id: string;
  /** The account's display name, e.g. "Everyday Checking". */
  name: string;
  /** Last four of the account number — never the full number. */
  mask: string;
  bankName: string;
  subtype: FinancialAccountSubtype;
}

export interface IPaymentAccountPickerBaseProps {
  accounts: readonly IPaymentAccountOption[];
  /** Null before a choice is made, or when the household has no eligible account. */
  selectedAccountId: string | null;
  onSelectAccount: (accountId: string) => void;
  onContinue: () => void;
  /** Opens a fresh Plaid Link session. */
  onLinkAnotherBank: () => void;
  /**
   * Diverts to the card rail, which rejoins the flow at plan selection.
   * Absent while the card rail has no surface — the link is hidden rather
   * than rendered dead.
   */
  onPayByCard?: () => void;
  /** Leaves checkout without subscribing; rendered only when the host provides it. */
  onCancel?: () => void;
}

/**
 * Screen 1 of checkout — which account the subscription debits.
 *
 * The linked bank account is the default payment method, so this opens on the
 * accounts the household already connected rather than a card form. When the
 * host wires `onPayByCard`, card is offered as a quiet alternative, not a
 * peer; with no callback the card option does not render at all.
 *
 * A household with no eligible account gets link-a-bank as the primary
 * action instead of an empty picker, with the card line only under a
 * card-wired host.
 */
const PaymentAccountPickerBase = ({
  accounts,
  selectedAccountId,
  onSelectAccount,
  onContinue,
  onLinkAnotherBank,
  onPayByCard,
  onCancel,
}: IPaymentAccountPickerBaseProps) => {
  const hasAccounts = accounts.length > 0;

  return (
    <div className="flex w-full flex-col gap-6">
      {hasAccounts ? (
        <>
          <RadioGroup
            aria-label="Account to debit"
            value={selectedAccountId ?? ''}
            onChange={onSelectAccount}
            className="w-full"
          >
            {accounts.map((account) => (
              <RadioButtonCard
                key={account.id}
                value={account.id}
                label={account.name}
                hint={`${account.bankName} ••${account.mask}`}
              />
            ))}
          </RadioGroup>

          <Button
            type="button"
            size="xl"
            color="primary"
            className="w-full"
            onClick={onContinue}
            isDisabled={selectedAccountId === null}
          >
            Continue
          </Button>
        </>
      ) : (
        <div className="flex w-full flex-col gap-4">
          <p className="text-tertiary text-sm">
            {onPayByCard !== undefined
              ? "You don't have a bank account we can debit yet. Connect one, or pay by card instead."
              : "You don't have a bank account we can debit yet. Connect one to continue."}
          </p>
          <Button type="button" size="xl" color="primary" className="w-full" onClick={onLinkAnotherBank}>
            Connect a bank account
          </Button>
          {onPayByCard !== undefined && (
            <Button type="button" size="md" color="link-gray" onClick={onPayByCard}>
              Pay by card instead
            </Button>
          )}
        </div>
      )}

      <div className="flex w-full flex-col items-center gap-3">
        {hasAccounts && (
          <Button type="button" size="md" color="link-gray" onClick={onLinkAnotherBank}>
            Link another bank
          </Button>
        )}
        {/* The empty state renders its own card button above, so only the
            account list needs the footer link. */}
        {hasAccounts && onPayByCard !== undefined && (
          <Button type="button" size="md" color="link-gray" onClick={onPayByCard}>
            Pay by card instead
          </Button>
        )}
        {onCancel !== undefined && (
          <Button type="button" size="md" color="link-gray" onClick={onCancel}>
            Cancel
          </Button>
        )}
        <p className="text-quaternary text-xs">Bank connection secured by Plaid</p>
      </div>
    </div>
  );
};

PaymentAccountPickerBase.displayName = 'PaymentAccountPickerBase';

export default PaymentAccountPickerBase;
